import { useEffect, useId, useMemo, useRef, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueries } from '@tanstack/react-query';
import { ArrowRight, Calendar, Clock, Loader2, MapPin, Plus, Search, Store } from 'lucide-react';
import type { SearchEntityType, SystemRole } from '@peles/shared';
import { EntityModal } from '../common/EntityModal';
import { searchEntities } from '../../api/search';
import { useRecentEvents } from '../../hooks/useRecentEvents';
import { NEW_EVENT_PATH, goTargetsFor } from './shortcuts';

/** Results per kind of record; the palette is for jumping, not browsing. */
export const PALETTE_RESULTS_PER_TYPE = 5;
const DEBOUNCE_MS = 200;

export interface PaletteItem {
  id: string;
  group: string;
  label: string;
  subtitle?: string;
  icon: ReactNode;
  to: string;
}

const SEARCHED: { type: SearchEntityType; group: string; path: string; icon: ReactNode }[] = [
  { type: 'event', group: 'Events', path: '/events/', icon: <Calendar size={16} /> },
  { type: 'vendor', group: 'Vendors', path: '/vendors/', icon: <Store size={16} /> },
  { type: 'venue', group: 'Venues', path: '/venues/', icon: <MapPin size={16} /> },
];

function matches(text: string, query: string): boolean {
  return text.toLowerCase().includes(query.toLowerCase());
}

function useDebounced(value: string, ms: number): string {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return debounced;
}

export interface CommandPaletteProps {
  open: boolean;
  onClose: () => void;
  role: SystemRole | undefined;
}

/**
 * Quick search (⌘K / Ctrl+K, or /). Empty, it offers the recently opened
 * events, "New event" and the pages; typed into, it searches events, vendors
 * and venues on the server alongside those, and Enter opens the highlighted
 * one. Arrow keys move, Escape closes.
 */
export function CommandPalette({ open, onClose, role }: CommandPaletteProps) {
  return (
    <EntityModal open={open} onClose={onClose} title="Search" size="sm">
      {/* Mounted per opening, so each search starts empty. */}
      {open && <PaletteBody onClose={onClose} role={role} />}
    </EntityModal>
  );
}

function PaletteBody({ onClose, role }: { onClose: () => void; role: SystemRole | undefined }) {
  const navigate = useNavigate();
  const { events: recent } = useRecentEvents();
  const [query, setQuery] = useState('');
  // The highlight follows a record, not a row number, so a refetch that
  // reorders the results never moves Enter onto a different record.
  const [activeKey, setActiveKey] = useState<string | null>(null);
  const listId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const trimmed = query.trim();
  const searchTerm = useDebounced(trimmed, DEBOUNCE_MS);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const results = useQueries({
    queries: SEARCHED.map((s) => ({
      queryKey: ['search', s.type, searchTerm, PALETTE_RESULTS_PER_TYPE],
      queryFn: () => searchEntities(s.type, searchTerm, PALETTE_RESULTS_PER_TYPE),
      enabled: searchTerm.length > 0,
      staleTime: 30_000,
    })),
  });
  const searching =
    trimmed.length > 0 && (trimmed !== searchTerm || results.some((r) => r.isFetching));
  // A search that failed is not a search that found nothing.
  const failed = !searching && trimmed.length > 0 && results.some((r) => r.isError);
  const retry = () => results.forEach((r) => r.isError && void r.refetch());

  const items = useMemo<PaletteItem[]>(() => {
    const actions: PaletteItem[] = [
      {
        id: 'action:new-event',
        group: 'Actions',
        label: 'New event',
        icon: <Plus size={16} />,
        to: NEW_EVENT_PATH,
      },
    ];
    const pages: PaletteItem[] = goTargetsFor(role).map((t) => ({
      id: `page:${t.to}`,
      group: 'Go to',
      label: t.label,
      subtitle: `G then ${t.key.toUpperCase()}`,
      icon: <ArrowRight size={16} />,
      to: t.to,
    }));

    if (!trimmed) {
      const recentItems: PaletteItem[] = recent.map((e) => ({
        id: `recent:${e.id}`,
        group: 'Recent events',
        label: e.name,
        icon: <Clock size={16} />,
        to: `/events/${e.id}`,
      }));
      return [...recentItems, ...actions, ...pages];
    }

    const found: PaletteItem[] = [];
    SEARCHED.forEach((s, i) => {
      // Only answers for the term on screen, never a stale one.
      if (searchTerm !== trimmed) return;
      for (const r of results[i].data?.results ?? []) {
        found.push({
          id: `${s.type}:${r.id}`,
          group: s.group,
          label: r.label,
          subtitle: r.subtitle || undefined,
          icon: s.icon,
          to: `${s.path}${r.id}`,
        });
      }
    });
    return [
      ...found,
      ...actions.filter((a) => matches(a.label, trimmed)),
      ...pages.filter((p) => matches(p.label, trimmed)),
    ];
  }, [trimmed, searchTerm, results, recent, role]);

  // A new term starts from the top; otherwise the highlighted record keeps
  // it, falling back to the top if that record has gone.
  useEffect(() => {
    setActiveKey(null);
  }, [trimmed]);
  const found = items.findIndex((item) => item.id === activeKey);
  const active = found === -1 ? 0 : found;

  // …and in view when the arrow keys take it past the edge of the list.
  useEffect(() => {
    document.getElementById(`${listId}-${active}`)?.scrollIntoView?.({ block: 'nearest' });
  }, [active, listId]);

  const choose = (item: PaletteItem | undefined) => {
    if (!item) return;
    onClose();
    navigate(item.to);
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      if (items.length === 0) return;
      const step = e.key === 'ArrowDown' ? 1 : -1;
      setActiveKey(items[(active + step + items.length) % items.length].id);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      choose(items[active]);
    }
  };

  const activeId = items[active] ? `${listId}-${active}` : undefined;
  let lastGroup = '';

  return (
    <div className="-mx-1">
      <div className="flex items-center gap-2 px-3 h-11 rounded-xl border border-gray-200 bg-gray-50 focus-within:border-primary-400 focus-within:bg-white">
        <Search size={16} className="text-gray-400 shrink-0" aria-hidden />
        <input
          ref={inputRef}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder="Search events, vendors, venues…"
          role="combobox"
          aria-label="Search"
          aria-expanded
          aria-controls={listId}
          aria-activedescendant={activeId}
          aria-autocomplete="list"
          className="flex-1 min-w-0 bg-transparent text-sm focus:outline-none"
        />
        {searching && (
          <Loader2 size={16} className="animate-spin text-gray-400" aria-label="Searching" />
        )}
      </div>

      <ul
        id={listId}
        role="listbox"
        aria-label="Results"
        className="mt-3 max-h-[60vh] overflow-auto"
      >
        {items.map((item, i) => {
          const header = item.group !== lastGroup ? item.group : null;
          lastGroup = item.group;
          const isActive = i === active;
          return (
            <li key={item.id} role="presentation">
              {header && (
                <div className="px-3 pt-3 pb-1 text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                  {header}
                </div>
              )}
              <div
                id={`${listId}-${i}`}
                role="option"
                aria-selected={isActive}
                onMouseMove={() => setActiveKey(item.id)}
                onClick={() => choose(item)}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg cursor-pointer text-sm ${
                  isActive ? 'bg-primary-50 text-primary-800' : 'text-gray-700'
                }`}
              >
                <span className={isActive ? 'text-primary-600' : 'text-gray-400'}>{item.icon}</span>
                <span className="flex-1 min-w-0 truncate font-medium">{item.label}</span>
                {item.subtitle && (
                  <span className="text-xs text-gray-400 truncate max-w-[40%]">
                    {item.subtitle}
                  </span>
                )}
              </div>
            </li>
          );
        })}
      </ul>
      {failed && (
        <div
          role="alert"
          className="mx-3 mt-2 flex items-center justify-between gap-3 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-900"
        >
          Search could not be completed.
          <button type="button" onClick={retry} className="font-semibold hover:underline">
            Try again
          </button>
        </div>
      )}
      {trimmed && !searching && !failed && items.length === 0 && (
        <p className="px-3 py-6 text-center text-sm text-gray-500">
          Nothing matches &ldquo;{trimmed}&rdquo;.
        </p>
      )}
    </div>
  );
}
